import { useCallback, useEffect, useRef, useState } from 'react';

import { apiGetFileLinkedRows } from '../api/datasetFetch';
import type { FileLinkedRowsResult } from '../types/datasetTypes';

type CacheEntry = {
  result?: FileLinkedRowsResult;
  promise?: Promise<FileLinkedRowsResult>;
};

const fileLinkedRowsCache = new Map<string, CacheEntry>();

const buildCacheKey = (rootFolderId: string, fileId: string) => `${rootFolderId}:${fileId}`;

export const invalidateFileLinkedRowsCache = (rootFolderId?: string, fileId?: string) => {
  if (!rootFolderId && !fileId) {
    fileLinkedRowsCache.clear();
    return;
  }

  for (const key of fileLinkedRowsCache.keys()) {
    if (
      (rootFolderId && key.startsWith(`${rootFolderId}:`))
      || (fileId && key.endsWith(`:${fileId}`))
    ) {
      fileLinkedRowsCache.delete(key);
    }
  }
};

export function useFileLinkedRows(
  fileId: string,
  rootFolderId: string,
  enabled: boolean,
  reloadToken = '',
) {
  const [result, setResult] = useState<FileLinkedRowsResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  const load = useCallback(async () => {
    if (!fileId || !rootFolderId) {
      setResult(null);
      return null;
    }

    const cacheKey = buildCacheKey(rootFolderId, fileId);
    const cached = fileLinkedRowsCache.get(cacheKey);
    if (cached?.result !== undefined && !cached.promise) {
      setResult(cached.result);
      setError(null);
      return cached.result;
    }

    if (cached?.promise) {
      setIsLoading(true);
      try {
        const resolved = await cached.promise;
        setResult(resolved);
        setError(null);
        return resolved;
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to load linked rows';
        setError(message);
        return null;
      } finally {
        setIsLoading(false);
      }
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setIsLoading(true);
    setError(null);

    const promise = apiGetFileLinkedRows(fileId, rootFolderId);
    fileLinkedRowsCache.set(cacheKey, { promise });

    try {
      const resolved = await promise;
      fileLinkedRowsCache.set(cacheKey, { result: resolved });
      if (requestIdRef.current === requestId) {
        setResult(resolved);
      }
      return resolved;
    } catch (err: unknown) {
      fileLinkedRowsCache.delete(cacheKey);
      const message = err instanceof Error ? err.message : 'Failed to load linked rows';
      if (requestIdRef.current === requestId) {
        setError(message);
        setResult(null);
      }
      return null;
    } finally {
      if (requestIdRef.current === requestId) {
        setIsLoading(false);
      }
    }
  }, [fileId, rootFolderId]);

  useEffect(() => {
    if (!enabled || !fileId || !rootFolderId) {
      setResult(null);
      setIsLoading(false);
      return;
    }

    fileLinkedRowsCache.delete(buildCacheKey(rootFolderId, fileId));
    void load();
  }, [enabled, fileId, load, reloadToken, rootFolderId]);

  return {
    result,
    isLoading,
    error,
    reload: load,
  };
}
